import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Upload, FileText, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";

/**
 * CoachCSV — two-tab CSV importer.
 *   1. Roster   → POST /csv/import-roster        (native ArmIQ roster format)
 *   2. Legacy   → POST /csv/import-competitor    (ArmCare / Armored Heat exports)
 */
export default function CoachCSV() {
  return (
    <div className="space-y-6 max-w-3xl" data-testid="coach-csv-page">
      <div>
        <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">CSV Import</div>
        <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter mt-1 text-white">
          Bring Your Data With You
        </h1>
        <p className="text-sm text-white/60 mt-2">
          Add players in bulk — or backfill years of legacy ArmCare and Armored Heat history in one upload.
        </p>
      </div>

      <Tabs defaultValue="roster">
        <TabsList className="bg-[var(--ink-2)] border border-[var(--ink-border)]">
          <TabsTrigger value="roster" data-testid="csv-tab-roster">Roster CSV</TabsTrigger>
          <TabsTrigger value="competitor" data-testid="csv-tab-competitor">ArmCare / Armored Heat</TabsTrigger>
        </TabsList>

        <TabsContent value="roster" className="mt-6">
          <RosterCsvImporter />
        </TabsContent>

        <TabsContent value="competitor" className="mt-6">
          <CompetitorCsvImporter />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ── Native ArmIQ Roster Upload ────────────────────────────── */
function RosterCsvImporter() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!file) return;
    setBusy(true); setError(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/csv/import-roster", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
    } catch (err) { setError(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-white/60">
        Required headers: <code className="bg-[var(--ink-2)] px-1.5 py-0.5 rounded text-xs text-white">email,name,year,dominant_arm,position,bats,throws,password</code>
      </p>

      <label className="block rounded-xl border-2 border-dashed border-[var(--ink-border)] p-12 text-center cursor-pointer hover:border-[var(--ink-border-strong)] transition-colors" style={{ backgroundColor: "var(--ink-2)" }} data-testid="csv-dropzone">
        <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="csv-file-input" />
        <Upload className="h-10 w-10 mx-auto text-white/40" />
        <div className="mt-3 font-semibold text-white">{file ? file.name : "Click to choose CSV"}</div>
        <div className="text-xs text-white/50 mt-1">UTF-8 encoded. Headers required.</div>
      </label>

      <div className="flex items-center gap-3">
        <Button onClick={submit} disabled={!file || busy} className="text-white font-semibold red-glow" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="csv-upload-button">
          <FileText className="h-4 w-4 mr-2" />
          {busy ? "Uploading…" : "Import roster"}
        </Button>
        {file && <button className="text-sm text-white/50 hover:text-white" onClick={() => { setFile(null); setResult(null); }}>Clear</button>}
      </div>

      {error && (
        <div className="rounded-lg border border-red-900/50 bg-red-950/40 p-4 text-sm text-red-300 flex gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5" /> {error}
        </div>
      )}
      {result && (
        <div className="rounded-lg border border-emerald-900/50 bg-emerald-950/30 p-4 text-sm text-emerald-200" data-testid="csv-result">
          <div className="flex items-center gap-2 font-semibold">
            <CheckCircle2 className="h-4 w-4" /> Import complete
          </div>
          <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
            <div><div className="text-xs text-emerald-400/70">Created</div><div className="font-display font-black text-3xl font-mono-num">{result.created}</div></div>
            <div><div className="text-xs text-emerald-400/70">Skipped</div><div className="font-display font-black text-3xl font-mono-num">{result.skipped}</div></div>
            <div><div className="text-xs text-emerald-400/70">Errors</div><div className="font-display font-black text-3xl font-mono-num">{result.errors?.length || 0}</div></div>
          </div>
          {result.errors?.length > 0 && (
            <ul className="mt-3 text-xs text-red-300 list-disc list-inside">
              {result.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/* ── ArmCare / Armored Heat backfill ───────────────────────── */
function CompetitorCsvImporter() {
  const [source, setSource] = useState("armcare");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!file) return;
    setBusy(true); setError(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source", source);
      const { data } = await api.post("/csv/import-competitor", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
    } catch (err) { setError(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const expected = source === "armcare"
    ? "athlete_email,date,er_strength,ir_strength,grip_strength,gird,soreness,sleep_quality,arm_health"
    : "athlete_email,date,throw_count,bullpen_count,long_toss_count,plyo_count,arm_feel,max_distance";

  return (
    <div className="space-y-6">
      <div className="rounded-xl ink-card p-5">
        <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase mb-2">Pick a legacy platform</div>
        <div className="grid grid-cols-2 gap-3">
          {[
            { v: "armcare",    title: "ArmCare",       sub: "Strength + Readiness exports" },
            { v: "armoredheat", title: "Armored Heat", sub: "Workload + throw counts" },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => { setSource(opt.v); setResult(null); }}
              className={`text-left rounded-lg border px-4 py-3 transition ${source === opt.v ? "border-[var(--brand-secondary)] bg-[rgba(217,4,41,0.06)]" : "border-[var(--ink-border)] bg-[var(--ink-2)] hover:border-white/30"}`}
              data-testid={`competitor-source-${opt.v}`}
            >
              <div className="font-display font-bold text-white">{opt.title}</div>
              <div className="text-xs text-white/55">{opt.sub}</div>
            </button>
          ))}
        </div>
      </div>

      <p className="text-sm text-white/60">
        Expected headers <span className="text-white/40">(extra columns are ignored)</span>:
        <code className="block mt-1 bg-[var(--ink-2)] px-2 py-1.5 rounded text-xs text-white break-all">{expected}</code>
      </p>

      <label className="block rounded-xl border-2 border-dashed border-[var(--ink-border)] p-12 text-center cursor-pointer hover:border-[var(--ink-border-strong)] transition-colors" style={{ backgroundColor: "var(--ink-2)" }} data-testid="competitor-csv-dropzone">
        <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="competitor-csv-file-input" />
        <Upload className="h-10 w-10 mx-auto text-white/40" />
        <div className="mt-3 font-semibold text-white">{file ? file.name : "Drop your CSV export"}</div>
        <div className="text-xs text-white/50 mt-1">Athletes are matched by <code className="text-white/70">athlete_email</code> against your roster.</div>
      </label>

      <div className="flex items-center gap-3">
        <Button onClick={submit} disabled={!file || busy} className="text-white font-semibold red-glow" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="competitor-upload-button">
          <FileText className="h-4 w-4 mr-2" />
          {busy ? "Importing…" : <>Backfill from {source === "armcare" ? "ArmCare" : "Armored Heat"} <ArrowRight className="h-4 w-4 ml-1" /></>}
        </Button>
        {file && <button className="text-sm text-white/50 hover:text-white" onClick={() => { setFile(null); setResult(null); }}>Clear</button>}
      </div>

      {error && (
        <div className="rounded-lg border border-red-900/50 bg-red-950/40 p-4 text-sm text-red-300 flex gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5" /> {error}
        </div>
      )}
      {result && (
        <div className="rounded-lg border border-emerald-900/50 bg-emerald-950/30 p-4 text-sm text-emerald-200" data-testid="competitor-result">
          <div className="flex items-center gap-2 font-semibold">
            <CheckCircle2 className="h-4 w-4" /> Backfill complete · {result.source}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
            <div><div className="text-xs text-emerald-400/70">Logs imported</div><div className="font-display font-black text-3xl font-mono-num">{result.imported_logs}</div></div>
            <div><div className="text-xs text-emerald-400/70">Profile updates</div><div className="font-display font-black text-3xl font-mono-num">{result.imported_profile_updates}</div></div>
            <div><div className="text-xs text-emerald-400/70">Errors</div><div className="font-display font-black text-3xl font-mono-num">{result.errors?.length || 0}</div></div>
          </div>
          {result.unmatched_emails?.length > 0 && (
            <div className="mt-4">
              <div className="text-xs text-amber-300 font-semibold">Unmatched athletes — add these to your roster first:</div>
              <ul className="mt-1 text-xs text-amber-200 list-disc list-inside">
                {result.unmatched_emails.slice(0, 20).map((e, i) => <li key={i}>{e}</li>)}
                {result.unmatched_emails.length > 20 && <li>…and {result.unmatched_emails.length - 20} more</li>}
              </ul>
            </div>
          )}
          {result.errors?.length > 0 && (
            <ul className="mt-3 text-xs text-red-300 list-disc list-inside">
              {result.errors.slice(0, 30).map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
